import React, { useState, useEffect } from "react";
import axios from 'axios';
import { ArrowLeft, Calendar, DollarSign, Plus, Minus, Search } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const API_BASE = "https://techiohisab.com/api";

export default function BalanceHistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({
    account_id: "",
    from_date: "",
    to_date: ""
  });
  const [error, setError] = useState(null);

  // Get account_id from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accountId = params.get('account_id');
    if (accountId) {
      setFilters(prev => ({ ...prev, account_id: accountId }));
    }
  }, [location.search]);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/networth/accounts`);
      setAccounts(res.data || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccounts([]);
    }
  };

  // Fetch balance history
  const fetchBalanceHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_BASE}/networth/balance/history`;
      
      // If specific account_id is provided, use the account-specific endpoint
      if (filters.account_id) {
        url = `${API_BASE}/networth/balance/history/${filters.account_id}`;
      }
      
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const res = await axios.get(url);
      setHistory(res.data || []);
    } catch (error) {
      console.error("Error fetching balance history:", error);
      setError("Failed to fetch balance history");
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    fetchBalanceHistory();
  };

  // Handle reset
  const handleReset = () => {
    setFilters({
      account_id: "",
      from_date: "",
      to_date: ""
    });
  };

  // Load data on mount and when filters change
  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchBalanceHistory();
  }, [filters]);

  return (
    <div className="page-container" style={{ padding: '20px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px', gap: '15px' }}>
        <button 
          onClick={() => navigate('/networth')}
          style={{ 
            padding: '8px 12px', 
            border: '1px solid #e2e8f0', 
            borderRadius: '6px', 
            backgroundColor: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
        >
          <ArrowLeft style={{ width: '16px', height: '16px' }} />
          Back to Net Worth
        </button>
        <h1 style={{ margin: 0, color: '#1a202c', fontSize: '24px', fontWeight: '600' }}>
          Balance Adjustment History
        </h1>
      </div>

      {/* Filters */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '12px', 
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              Account
            </label>
            <select
              value={filters.account_id}
              onChange={(e) => setFilters(prev => ({ ...prev, account_id: e.target.value }))}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: '1px solid #d1d5db', 
                borderRadius: '6px',
                backgroundColor: 'white'
              }}
            >
              <option value="">All Accounts</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              From Date
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters(prev => ({ ...prev, from_date: e.target.value }))}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: '1px solid #d1d5db', 
                borderRadius: '6px',
                backgroundColor: 'white'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              To Date
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters(prev => ({ ...prev, to_date: e.target.value }))}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: '1px solid #d1d5db', 
                borderRadius: '6px',
                backgroundColor: 'white'
              }}
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={handleSearch}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#3b82f6', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <Search style={{ width: '16px', height: '16px' }} />
            Search
          </button>
          <button 
            onClick={handleReset}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#6b7280', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* History Table */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            Loading balance history...
          </div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
            {error}
          </div>
        ) : history.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            No balance adjustment history found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Account</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Old Balance</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Change</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>New Balance</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Note</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Done By</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr key={record.id} style={{ borderBottom: '1px solid #e5e7eb', '&:hover': { backgroundColor: '#f9fafb' } }}>
                    <td style={{ padding: '12px', color: '#374151' }}>
                      {formatDate(record.created_at)}
                    </td>
                    <td style={{ padding: '12px', color: '#374151', fontWeight: '500' }}>
                      {record.account_name || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: record.adjustment_type === 'manual_add' ? '#dcfce7' : '#fee2e2',
                        color: record.adjustment_type === 'manual_add' ? '#166534' : '#991b1b',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {record.adjustment_type === 'manual_add' ? (
                          <>
                            <Plus style={{ width: '12px', height: '12px' }} />
                            Add
                          </>
                        ) : (
                          <>
                            <Minus style={{ width: '12px', height: '12px' }} />
                            Subtract
                          </>
                        )}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: '#374151' }}>
                      {formatCurrency(record.old_balance)}
                    </td>
                    <td style={{ padding: '12px', color: record.adjustment_type === 'manual_add' ? '#166534' : '#991b1b', fontWeight: '500' }}>
                      {record.adjustment_type === 'manual_add' ? '+' : '-'}{formatCurrency(record.change_amount)}
                    </td>
                    <td style={{ padding: '12px', color: '#374151', fontWeight: '500' }}>
                      {formatCurrency(record.new_balance)}
                    </td>
                    <td style={{ padding: '12px', color: '#374151', maxWidth: '200px' }}>
                      <div style={{ wordBreak: 'break-word' }}>
                        {record.note || 'No note'}
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: '#374151' }}>
                      {record.created_by || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
